import {
  Alert,
  Button,
  Group,
  PasswordInput,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import axios from 'axios';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ExclamationMark } from 'tabler-icons-react';
import { z } from 'zod';

import { useAcceptInvite } from 'api/invites/acceptInvite';

import AuthLayout from './AuthLayout';

function AcceptInviteForm() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const acceptInviteMutation = useAcceptInvite();

  const inviteSchema = useMemo(
    () =>
      z.object({
        firstName: z
          .string()
          .trim()
          .min(1, { message: t('auth.validation.firstNameRequired') }),
        lastName: z
          .string()
          .trim()
          .min(1, { message: t('auth.validation.lastNameRequired') }),
        password: z
          .string()
          .min(6, { message: t('auth.validation.passwordMin') }),
      }),
    [t]
  );

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      password: '',
    },
    validate: zodResolver(inviteSchema),
  });

  const handleSubmit = (values: typeof form.values) => {
    acceptInviteMutation.mutate(
      {
        token,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
      },
      {
        onSuccess: () => {
          showNotification({
            title: t('auth.invite.acceptedTitle'),
            message: t('auth.invite.acceptedBody'),
            color: 'teal',
          });
          navigate('/login', { replace: true });
        },
        onError: (err) => {
          if (axios.isAxiosError(err)) {
            const { response } = err;
            if (response?.data) {
              const { status } = response;
              const data = response.data as { message?: string };
              const message = data?.message;
              if (status === 404) {
                showNotification({
                  title: t('auth.invite.expiredTitle'),
                  message: t('auth.invite.expiredBody'),
                  color: 'red',
                  icon: <ExclamationMark />,
                });
                return;
              }
              if (status === 403 && message) {
                showNotification({
                  title: t('auth.invite.cannotJoinTitle'),
                  message,
                  color: 'orange',
                  icon: <ExclamationMark />,
                });
                return;
              }
            }
          }
          showNotification({
            title: t('common.error'),
            message: t('auth.invite.acceptFailed'),
            color: 'red',
            icon: <ExclamationMark />,
          });
        },
      }
    );
  };

  if (!token) {
    return (
      <AuthLayout title="Invite">
        <Alert color="red" icon={<AlertCircle color="#E03131" />} mb={20}>
          <Text color="red.9">{t('auth.invite.missingToken')}</Text>
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Complete Your Profile">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Group grow>
          <TextInput
            label={t('auth.requestAccess.firstName')}
            mb={20}
            {...form.getInputProps('firstName')}
          />
          <TextInput
            label={t('auth.requestAccess.lastName')}
            mb={20}
            {...form.getInputProps('lastName')}
          />
        </Group>
        <PasswordInput
          label={t('auth.password')}
          mb={20}
          {...form.getInputProps('password')}
        />
        <Button
          type="submit"
          loading={
            acceptInviteMutation.isLoading || acceptInviteMutation.isSuccess
          }
          w="100%"
          size="md"
          color="violet.5"
        >
          {t('auth.invite.createAccount')}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default AcceptInviteForm;
